/**
 * Utilities for parsing @mention syntax in chat messages.
 *
 * Supports:
 * - @name          -> search by name, mention all services
 * - @slack:name    -> search by name, mention Slack only
 * - @email:a@b.com -> search by EMAIL (not name) across all services
 * - @!name         -> internal reference only (no notification)
 *
 * Note: `@email:` is a SEARCH MODE (search by email), not a service target.
 * Use `@email:john@example.com` to narrow results by exact email match.
 */

export interface ParsedMention {
  /** Original text, e.g., "@john" or "@slack:john" */
  raw: string;
  /** Username/email part, e.g., "john" */
  identifier: string;
  /** Explicit service target, e.g., "slack" (NOT email - that's a search mode) */
  targetService?: string;
  /** Search mode: 'name' (default) or 'email' */
  searchMode: "name" | "email";
  /** @!john style - internal only, no notification */
  isInternal: boolean;
  /** Start position in text */
  startIndex: number;
  /** End position in text */
  endIndex: number;
}

export interface MentionTrigger {
  /** Whether mention autocomplete should be active */
  active: boolean;
  /** The search query (text after @) */
  query: string;
  /** Position of the @ character */
  startIndex: number;
  /** Optional service prefix if detected (slack, teams, google - NOT email) */
  servicePrefix?: string;
  /** Search mode: 'name' (default) or 'email' (when @email: prefix used) */
  searchMode: "name" | "email";
}

/** Supported service types for explicit targeting (NOT email - that's a search mode) */
export const SUPPORTED_SERVICES = ["slack", "teams", "google"] as const;
export type SupportedService = (typeof SUPPORTED_SERVICES)[number];

/** Prefix that changes search mode to email-based search */
export const EMAIL_SEARCH_PREFIX = "email";

/**
 * Regex pattern for mention syntax:
 * - @name          -> search by name, mention all services
 * - @slack:name    -> search by name, mention Slack only
 * - @email:a@b.com -> search by EMAIL across all services
 * - @!name         -> internal reference only
 *
 * Note: `email:` is a search mode prefix, not a service target.
 */
const MENTION_PATTERN = /@(!)?(?:(\w+):)?([a-zA-Z0-9._@-]+)/g;

/**
 * Parse all mentions from a text string.
 *
 * @param text - The text to parse
 * @returns Array of parsed mentions
 *
 * @example
 * parseMentions("Hey @john and @slack:jane and @email:jane@example.com")
 * // Returns:
 * // [
 * //   { raw: "@john", identifier: "john", searchMode: "name", isInternal: false, ... },
 * //   { raw: "@slack:jane", identifier: "jane", targetService: "slack", searchMode: "name", ... },
 * //   { raw: "@email:jane@example.com", identifier: "jane@example.com", searchMode: "email", ... }
 * // ]
 */
export function parseMentions(text: string): ParsedMention[] {
  const mentions: ParsedMention[] = [];
  let match: RegExpExecArray | null;

  // Reset regex state
  MENTION_PATTERN.lastIndex = 0;

  while ((match = MENTION_PATTERN.exec(text)) !== null) {
    const [raw, internalFlag, prefix, identifier] = match;
    const prefixLower = prefix?.toLowerCase();

    // Determine if this is a service target or email search mode
    let targetService: string | undefined;
    let searchMode: "name" | "email" = "name";

    if (prefixLower === EMAIL_SEARCH_PREFIX) {
      // `@email:john@example.com` -> search by email across all services
      searchMode = "email";
      // No targetService - email search applies to all services
    } else if (prefix && SUPPORTED_SERVICES.includes(prefixLower as SupportedService)) {
      // `@slack:john` -> search by name, target specific service
      targetService = prefixLower;
    } else if (prefix) {
      // Unknown prefix, skip this match
      continue;
    }

    mentions.push({
      raw,
      identifier,
      targetService,
      searchMode,
      isInternal: !!internalFlag,
      startIndex: match.index,
      endIndex: match.index + raw.length,
    });
  }

  return mentions;
}

/**
 * Detect if the user is typing a mention at the cursor position.
 *
 * Used to trigger the autocomplete dropdown when user types @.
 *
 * @param text - The full text content
 * @param cursorPosition - Current cursor position
 * @returns Mention trigger info if active, null otherwise
 *
 * @example
 * detectMentionTrigger("Hello @jo", 9)
 * // Returns: { active: true, query: "jo", startIndex: 6, searchMode: "name" }
 *
 * detectMentionTrigger("Hello @email:jane@", 18)
 * // Returns: { active: true, query: "jane@", startIndex: 6, searchMode: "email" }
 *
 * detectMentionTrigger("user@example.com", 16)
 * // Returns: null (@ not preceded by whitespace)
 */
export function detectMentionTrigger(text: string, cursorPosition: number): MentionTrigger | null {
  // Look backwards from cursor to find @
  const textBeforeCursor = text.slice(0, cursorPosition);
  const lastAtIndex = textBeforeCursor.lastIndexOf("@");

  if (lastAtIndex === -1) {
    return null;
  }

  // Check if @ is at start or preceded by whitespace
  if (lastAtIndex > 0 && !/\s/.test(textBeforeCursor[lastAtIndex - 1])) {
    return null;
  }

  const queryWithPrefix = textBeforeCursor.slice(lastAtIndex + 1);

  // Don't trigger if there's whitespace after @ (mention already completed)
  if (/\s/.test(queryWithPrefix)) {
    return null;
  }

  // Check for prefix (e.g., "slack:" or "email:" in "@slack:jo" or "@email:jane@")
  let query = queryWithPrefix;
  let servicePrefix: string | undefined;
  let searchMode: "name" | "email" = "name";

  const colonIndex = queryWithPrefix.indexOf(":");

  if (colonIndex > 0) {
    const potentialPrefix = queryWithPrefix.slice(0, colonIndex).toLowerCase();

    if (potentialPrefix === EMAIL_SEARCH_PREFIX) {
      // @email: is a search mode, not a service target
      searchMode = "email";
      query = queryWithPrefix.slice(colonIndex + 1);
    } else if (SUPPORTED_SERVICES.includes(potentialPrefix as SupportedService)) {
      servicePrefix = potentialPrefix;
      query = queryWithPrefix.slice(colonIndex + 1);
    }
  }

  // Check for internal prefix (!)
  if (query.startsWith("!")) {
    query = query.slice(1);
  }

  return {
    active: true,
    query,
    startIndex: lastAtIndex,
    servicePrefix,
    searchMode,
  };
}

/**
 * Replace mention text with the selected identity's display name.
 *
 * @param text - Original text
 * @param startIndex - Position of the @ character
 * @param cursorPosition - Current cursor position
 * @param replacement - The replacement text (e.g., "@John Smith")
 * @returns New text and cursor position
 *
 * @example
 * replaceMention("Hello @jo", 6, 9, "@John Smith")
 * // Returns: { text: "Hello @John Smith ", newCursorPosition: 18 }
 */
export function replaceMention(
  text: string,
  startIndex: number,
  cursorPosition: number,
  replacement: string,
): { text: string; newCursorPosition: number } {
  const before = text.slice(0, startIndex);
  const after = text.slice(cursorPosition);
  const newText = `${before}${replacement} ${after}`;

  return {
    text: newText,
    newCursorPosition: before.length + replacement.length + 1,
  };
}

/**
 * Structured mention data for message payload.
 */
export interface StructuredMention {
  /** Reference to UnifiedIdentity.id */
  identity_id: string;
  /** Text shown in message (e.g., "@John Smith") */
  display_text: string;
  /** Full name resolved from identity */
  resolved_name: string;
  /** Canonical email from unified identity */
  canonical_email?: string;
  /** Specific services to target, null = all */
  target_services?: string[];
  /** Delivery mode */
  target_mode: "all" | "internal";
  /** Service-specific user IDs for direct API queries (e.g., { jira: "accountId123" }) */
  service_user_ids?: Record<string, string>;
}

/**
 * Identity information for mention resolution.
 */
export interface ResolvedIdentity {
  id: string;
  displayName: string;
  canonicalEmail: string;
}

/**
 * Convert parsed mentions to structured data for message payload.
 *
 * @param mentions - Parsed mentions from text
 * @param resolvedIdentities - Map of identifier to resolved identity
 * @returns Structured mentions for API payload
 */
export function buildStructuredMentions(
  mentions: ParsedMention[],
  resolvedIdentities: Map<string, ResolvedIdentity>,
): StructuredMention[] {
  return mentions
    .filter((m) => {
      // Find by identifier or email
      return (
        resolvedIdentities.has(m.identifier) || resolvedIdentities.has(m.identifier.toLowerCase())
      );
    })
    .map((m) => {
      const identity =
        resolvedIdentities.get(m.identifier) || resolvedIdentities.get(m.identifier.toLowerCase())!;

      return {
        identity_id: identity.id,
        display_text: m.raw,
        resolved_name: identity.displayName,
        canonical_email: identity.canonicalEmail,
        target_services: m.targetService ? [m.targetService] : undefined,
        target_mode: m.isInternal ? "internal" : "all",
      };
    });
}

/**
 * Extract mention display names from text for highlighting.
 *
 * @param text - Message text with mentions
 * @returns Array of display names that are mentioned
 */
export function extractMentionDisplayNames(text: string): string[] {
  const mentions = parseMentions(text);

  return mentions.map((m) => m.identifier);
}

/**
 * Check if a character position is within a mention.
 *
 * @param text - The text content
 * @param position - Character position to check
 * @returns The mention at that position, or null
 */
export function getMentionAtPosition(text: string, position: number): ParsedMention | null {
  const mentions = parseMentions(text);

  return mentions.find((m) => position >= m.startIndex && position <= m.endIndex) || null;
}
